import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { Mail, Phone, Eye, EyeOff } from 'lucide-react'
import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js'
import { useAuth } from '../../contexts/AuthContext'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import TopNavigation from '../../../components/TopNavigation.jsx'

const LoginPage: React.FC = () => {
  const { signIn, signInWithPhone, user, loading: authLoading, secretUser } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const from = location.state?.from?.pathname || '/dashboard'

  const [method, setMethod] = useState<'email' | 'phone'>('email')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    password: ''
  })

  const authStateRef = useRef<{ isSigningIn: boolean }>({ isSigningIn: false })

  // Handle URL parameters for method selection
  useEffect(() => {
    const t = searchParams.get('t')
    if (t === 'mobile') {
      setMethod('phone')
    } else if (t === 'email') {
      setMethod('email')
    }
  }, [searchParams])

  // Effect to handle navigation after successful authentication
  useEffect(() => {
    console.log('[LoginPage] useEffect check:', {
      isSigningIn: authStateRef.current.isSigningIn,
      hasUser: !!user,
      authLoading,
      hasSecretUser: !!secretUser,
      secretUserId: secretUser?.id
    })

    if (authStateRef.current.isSigningIn && user && !authLoading && secretUser) {
      // User is authenticated, auth loading is complete, AND secretUser is loaded
      authStateRef.current.isSigningIn = false
      setLoading(false)

      console.log('[LoginPage] Navigation check:', {
        userEmailConfirmedAt: user.email_confirmed_at,
        secretUserEmailVerified: secretUser?.email_verified,
        userEmail: user.email
      })

      // Check Supabase auth status first - if email is confirmed in auth.users, trust it
      const isEmailConfirmed = user.email_confirmed_at || secretUser?.email_verified === true

      console.log('[LoginPage] isEmailConfirmed:', isEmailConfirmed)

      if (!isEmailConfirmed && user.email) {
        // Only redirect to verify if email is truly not confirmed
        console.log('[LoginPage] Redirecting to verify page')
        navigate(`/verify?email=${encodeURIComponent(user.email)}`, { replace: true })
      } else {
        console.log('[LoginPage] Redirecting to:', from)
        navigate(from, { replace: true })
      }
    }
  }, [user, authLoading, navigate, from, secretUser])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    authStateRef.current.isSigningIn = true

    try {
      if (method === 'email') {
        await signIn(formData.email, formData.password)
      } else {
        // Format phone number to E.164 for login
        let phoneToUse = formData.phone
        try {
          if (isValidPhoneNumber(formData.phone, 'US')) {
            const phoneNumber = parsePhoneNumber(formData.phone, 'US')
            phoneToUse = phoneNumber.format('E.164')
          }
        } catch (err) {
          console.log('Phone parsing failed, using as-is:', err)
        }
        await signInWithPhone(phoneToUse, formData.password)
      }
      // Don't navigate here - let the useEffect handle it after auth state updates
    } catch (error) {
      console.error('Login error:', error)
      authStateRef.current.isSigningIn = false
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen-safe bg-background flex flex-col">
      {/* Header */}
      <TopNavigation />

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-foreground mb-2">Welcome Back</h1>
            <p className="text-muted-foreground">Sign in to your SECRET account</p>
          </div>

          {/* Show signup success message if redirected from registration */}
          {location.state?.message && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-700 text-center">
                {location.state.message}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Login Method Toggle */}
            <div className="flex rounded-lg bg-muted p-1">
              <button
                type="button"
                onClick={() => setMethod('email')}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all ${
                  method === 'email'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Mail size={16} className="inline mr-2" />
                Email
              </button>
              <button
                type="button"
                onClick={() => setMethod('phone')}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all ${
                  method === 'phone'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Phone size={16} className="inline mr-2" />
                Phone
              </button>
            </div>

            {/* Contact Field */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {method === 'email' ? 'Email Address' : 'Phone Number'}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  {method === 'email' ? (
                    <Mail size={20} className="text-muted-foreground" />
                  ) : (
                    <Phone size={20} className="text-muted-foreground" />
                  )}
                </div>
                <input
                  type={method === 'email' ? 'email' : 'tel'}
                  value={method === 'email' ? formData.email : formData.phone}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    [method]: e.target.value 
                  }))}
                  placeholder={method === 'email' ? 'your@email.com' : '+1 (555) 123-4567'}
                  className="w-full px-3 py-2 pl-12 bg-transparent border border-border text-foreground rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    password: e.target.value 
                  }))}
                  placeholder="Enter your password"
                  className="w-full px-3 py-2 pr-10 bg-transparent border border-border text-foreground rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="bg-primary text-primary-foreground hover:bg-primary/90 w-full flex items-center justify-center px-6 py-3 text-sm font-medium rounded-md transition-colors disabled:opacity-50"
            >
              {loading ? <LoadingSpinner size="sm" /> : 'Sign In to SECRET'}
            </button>
          </form>

          {/* Footer Links - Immediately below the form, properly centered */}
          <div className="mt-6 space-y-4">
            {/* Forgot Password Link */}
            <div className="flex justify-center">
              <Link 
                to="/forgot-password" 
                className="text-sm text-primary hover:text-primary/80 transition-colors"
              >
                Forgot password?
              </Link>
            </div>

            {/* Don't have account Link */}
            <div className="flex justify-center">
              <span className="text-sm text-muted-foreground">
                Don't have an account?{' '}
                <Link 
                  to="/register" 
                  className="text-primary hover:text-primary/80 font-medium transition-colors"
                >
                  Join SECRET
                </Link>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginPage